import { FC, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next"; // Import useTranslation

import { ROUTES } from "constants/routes";
import { useWindowSize } from "hooks/window-size";

import Activity from "icons/Activity";
import ActivityFilled from "icons/ActivityFilled";
import Exit from "icons/Exit";

import { Avatar } from "../Avatar";
import { Drawer } from "../Drawer";
import { Tooltip } from "../Tooltip";

import {
  StyledMobileMenu,
  StyledMobileMenuContainer,
  StyledMobileMenuFooter,
  StyledMobileMenuItem,
  StyledMobileMenuItemBtn,
  StyledMobileMenuItemIcon,
  StyledMobileMenuLogOutBtn,
  StyledMobileMenuNav,
  StyledMobileMenuUser,
  StyledMobileMenuUserInfo,
  StyledMobileMenuVersionContainer,
} from "styles/components/mobile-menu";

type SidebarNavItem = {
  title: string;
  route?: string;
  icon?: JSX.Element;
  activeIcon?: JSX.Element;
  disabled?: boolean;
};

type User = {
  avatar?: string;
  name: string;
};

interface SidebarProps {
  nav?: SidebarNavItem[];
  user?: User;
  activeNavBtn?: string;
  availability?: boolean;
  open?: boolean;
  version?: string;

  onClose?: () => void;
  onUserClick?: () => void;
  setActiveNavBtn?: (title: string) => void;
  setAvailability?: (availability: boolean) => void;
  onLogOut?: () => void;
}

const initialSidebarNav: SidebarNavItem[] = [
  {
    title: "my_experiments",
    route: ROUTES.home,
    icon: <Activity />,
    activeIcon: <ActivityFilled />,
  },
];

const MobileMenu: FC<SidebarProps> = ({
  nav = initialSidebarNav,
  activeNavBtn = nav[0].title,
  availability,
  user,
  open,
  version,
  onClose,
  onUserClick,
  setActiveNavBtn,
  setAvailability,
  onLogOut,
}) => {
  const { windowSize } = useWindowSize();
  const { t } = useTranslation(); // Initialize translation
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    if (open && windowSize.width >= 1024) {
      onClose?.();
    }
  }, [onClose, open, windowSize.width]);

  const handleNavBtnClick = useCallback(
    (title: string, route?: string) => () => {
      if (route) {
        navigate(route);
      }

      setActiveNavBtn?.(title);
    },
    [navigate, setActiveNavBtn]
  );

  const renderedVersionTooltipText = useMemo(
    () => (version && version.length > 30 ? version : undefined),
    [version]
  );

  const renderedVersion = useMemo(
    () =>
      version ? (
        <StyledMobileMenuVersionContainer>
          <Tooltip
            text={renderedVersionTooltipText}
            position="right"
            size="auto"
          >
            <Typography
              variant="bodyM"
              fontWeight={400}
              textOverflow="ellipsis"
              width="100%"
              display="block"
              whiteSpace="nowrap"
              overflow="hidden"
              color={theme.palette.customGrey.main}
            >
              {version}
            </Typography>
          </Tooltip>
        </StyledMobileMenuVersionContainer>
      ) : null,
    [renderedVersionTooltipText, theme.palette.customGrey.main, version]
  );

  const renderedFooter = useMemo(
    () =>
      user && user.name ? (
        <StyledMobileMenuFooter>
          <StyledMobileMenuUser onClick={onUserClick}>
            <Avatar src={user.avatar} name={user.name} alt={user.name} />
            <StyledMobileMenuUserInfo>
              <Typography variant="bodyS" fontWeight={300}>
                {t("taster")}
              </Typography>
              <Typography
                variant="bodyM"
                fontWeight={400}
                textOverflow="ellipsis"
                width="100%"
                display="block"
                whiteSpace="nowrap"
                overflow="hidden"
              >
                {user.name}
              </Typography>
            </StyledMobileMenuUserInfo>
          </StyledMobileMenuUser>
          <StyledMobileMenuLogOutBtn onClick={onLogOut}>
            <span className="log-out-btn__icon">
              <Exit />
            </span>
            <span className="log-out-btn__text">{t("log_out")}</span>
          </StyledMobileMenuLogOutBtn>
          {renderedVersion}
        </StyledMobileMenuFooter>
      ) : null,
    [user, onUserClick, t, onLogOut, renderedVersion]
  );

  return (
    <Drawer
      open={open && windowSize.width < 1024}
      width="300px"
      overflow={windowSize.height > 400 ? "visible" : "auto"}
      onClose={onClose}
    >
      <StyledMobileMenu>
        <StyledMobileMenuContainer>
          <StyledMobileMenuNav>
            {nav?.map(({ title, route, icon, activeIcon, disabled }) => (
              <StyledMobileMenuItem className="mobile-menu-item" key={title}>
                <StyledMobileMenuItemBtn
                  className="mobile-menu-item__btn"
                  onClick={handleNavBtnClick(title, route)}
                  active={t(title) === t(activeNavBtn) ? 1 : 0}
                  disabled={disabled}
                >
                  <StyledMobileMenuItemIcon className="mobile-menu-item__icon">
                    {t(title) === t(activeNavBtn) ? activeIcon ?? icon : icon}
                  </StyledMobileMenuItemIcon>
                  {t(title)}
                </StyledMobileMenuItemBtn>
              </StyledMobileMenuItem>
            ))}
          </StyledMobileMenuNav>
        </StyledMobileMenuContainer>
        {renderedFooter}
      </StyledMobileMenu>
    </Drawer>
  );
};

export default MobileMenu;
